import shuffle from 'lodash/shuffle';

import {
  AGGREGATION_TYPES as CLIENT_SIDE_AGGREGATION_TYPES,
  CONTRIBUTORS,
  PRIMARY_PROFESSIONS_SLUGS,
  PROFESSION_IDS,
  SEARCH_ENTITY_TYPE_MAP,
  WORK_ROLE_TYPE_IDS,
} from 'constants/index';

import { getLanguageCanonicalName, getOption } from 'utils/common';
import { getCityCountry } from 'utils/globals';
import { condenseImageObject, expandImageObject } from 'utils/globals/imageOptimizatons';
import { getWorkTitle } from 'utils/works';

import { AGGREGATION_TYPES, FILTER_TYPES, TP } from 'operabase-router/constants';

export const selectData = response => response?.data;

export const selectTotalCount = response => ({ total: response?.total });

export const transformListToSingleData = response => ({
  ...response?.data,
  data: response?.data?.data?.[0],
});

export const transformGetRandomisedOrderedList = response => {
  const total = response?.data?.length || 0;
  const order = shuffle(Array.from({ length: total }, (_i, i) => i + 1));

  return {
    ...response,
    data: response?.data?.map((item, index) => ({ ...item, order: order[index] || 0 })) || [],
  };
};

export const transformSearchResultsToEntity = response => ({
  ...response,
  data: response?.data?.map(result => {
    const { entity, entity_type, ...rest } = result || {};

    return {
      entityType: SEARCH_ENTITY_TYPE_MAP[entity_type],
      entity: {
        name: entity,
        ...rest,
      },
    };
  }),
});

export const transformLanguageResults = response => ({
  data: response?.data?.data?.map(lang => ({
    ...lang,
    ...getOption(lang, 'id', getLanguageCanonicalName),
  })),
});

export const transformDynamicFiltersForCastingTool = ({
  contributionTypes,
  primaryProfession,
  cast,
  crew,
  instrumentalist,
  ensemble,
}) => {
  // NOTE: Get producer, venue and co-producer info by transforming contribution types
  const [producerInfo, venueInfo, coProducerInfo] =
    transformDynamicFiltersList({
      list: contributionTypes?.data || [],
    })?.flat() || [];

  // NOTE: Transform ensemble data
  const { [CONTRIBUTORS.ENSEMBLE]: ensembleInfo } =
    transformDynamicFiltersGroup({
      list: [...(ensemble?.data?.data || [])],
      groups: {
        default: {
          label: FILTER_TYPES.ENSEMBLE,
          slug: FILTER_TYPES.ENSEMBLE,
          total: ensemble?.data?.total,
          type: FILTER_TYPES.ENSEMBLE,
        },
      },
      aggType: AGGREGATION_TYPES[FILTER_TYPES.AGG_ENSEMBLE_TYPE_ID],
    }) || [];

  // NOTE: Get conductor, director and choreographer info from primary professions
  const [director, conductorInfo, choreographerInfo] =
    transformDynamicFiltersList({
      list: primaryProfession?.data,
      category: FILTER_TYPES.ROLE,
    })?.flat() || [];

  // NOTE: Transform cast, crew and instrumentalist data into groups
  // Since API doesn't return parentProfession data, we need to group them manually based on API call context
  const castInfo = cast?.total > 0 ? {
    slug: FILTER_TYPES.CAST,
    label: FILTER_TYPES.CAST,
    total: (cast.data || []).reduce((sum, item) => sum + (item?.contribCount || 0), 0),
    type: FILTER_TYPES.ROLE,
    data: cast.data || [],
    filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
    aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
  } : null;

  const instrumentalistInfo = instrumentalist?.total > 0 ? {
    slug: FILTER_TYPES.INSTRUMENTALIST,
    label: FILTER_TYPES.INSTRUMENTALIST,
    total: (instrumentalist.data || []).reduce((sum, item) => sum + (item?.contribCount || 0), 0),
    type: FILTER_TYPES.ROLE,
    data: instrumentalist.data || [],
    filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
    aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
  } : null;

  const crewInfo = crew?.total > 0 ? {
    slug: FILTER_TYPES.CREW,
    label: FILTER_TYPES.CREW,
    total: (crew.data || []).reduce((sum, item) => sum + (item?.contribCount || 0), 0),
    type: FILTER_TYPES.ROLE,
    data: crew.data || [],
    filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
    aggregationType: AGGREGATION_TYPES[FILTER_TYPES.AGG_PROFESSION_ID],
  } : null;
  // NOTE: Combine all values and filter out any falsy values

  return [
    [
      producerInfo && {
        ...producerInfo,
        subFilterApiParams: { [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: producerInfo?.slug },
      },
      coProducerInfo && coProducerInfo,
      venueInfo && venueInfo,
    ]?.filter(Boolean),
    [
      director && director,
      conductorInfo && conductorInfo,
      choreographerInfo && choreographerInfo,
      castInfo?.total > 0 && {
        ...castInfo,
        subFilterApiParams: { work_role_type_id: WORK_ROLE_TYPE_IDS.CHARACTER },
      },
      crewInfo?.total > 0 && {
        ...crewInfo,
        subFilterApiParams: { [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: crewInfo?.[0]?.slug },
      },
      instrumentalistInfo?.total > 0 && {
        ...instrumentalistInfo,
        subFilterApiParams: { work_role_type_id: WORK_ROLE_TYPE_IDS.INSTRUMENTALIST },
      },
      ensembleInfo?.total > 0 && {
        ...ensembleInfo,
        subFilterApiParams: { [AGGREGATION_TYPES[FILTER_TYPES.AGG_CONTRIBUTION_TYPE]]: ensembleInfo?.slug },
      },
    ]?.filter(Boolean),
  ];
};

export const transformDynamicFilters = results => {
  const list1 = transformDynamicFiltersList({ list: results[0] });
  const list2 = transformDynamicFiltersList({
    list: results[1],
    aggType: AGGREGATION_TYPES.CONTRIBUTOR_ID,
    category: FILTER_TYPES.ENSEMBLE,
  });
  const list3 = transformDynamicFiltersList({
    list: results[2],
    aggType: AGGREGATION_TYPES.CONTRIBUTOR_ID,
    category: FILTER_TYPES.ROLE,
  });
  return [[...(list1[0] || []), ...(list2[0] || [])], ...(list3 || [])];
};

const transformDynamicFiltersGroup = ({ list, aggType, groups }) => {
  if (!list?.length) {
    return [];
  }

  // NOTE: Process each subarray in the list separately
  const buckets = list.reduce((acc, item) => {
    // NOTE: Skip items that are primary professions
    if (PRIMARY_PROFESSIONS_SLUGS.includes(item.slug)) {
      return acc;
    }

    // NOTE: Group items by parentProfession id into buckets
    const parentId = item.parentProfession?.id || 'default';

    if (item?.id === parentId) {
      return acc;
    }

    // NOTE: Initialize bucket if it doesn't exist
    if (!acc[parentId]) {
      acc[parentId] = {
        data: [],
        total: 0,
      };
    }

    // NOTE: Add item to appropriate bucket with necessary transformations
    acc[parentId].data.push(item);
    acc[parentId].total += item?.contribCount || item?.total || 0;

    return acc;
  }, {});

  // NOTE: Transform bucket keys into array of name objects with their corresponding items
  const transformedBuckets = Object.entries(buckets).reduce((acc, [parentId]) => {
    const group = groups[parentId] || groups.default;
    acc[group.slug] = {
      ...group,
      ...buckets[parentId],
      filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
      aggregationType: aggType,
    };
    return acc;
  }, {});

  return transformedBuckets;
};

const transformDynamicFiltersList = ({ list, aggType: DEFAULT_AGG_TYPE, category }) => {
  if (!list?.length) {
    return [];
  }

  const primaryProfessionsGroupKey = 'PRIMARY_PROFESSIONS';
  const commonGroupKey = 'COMMON_GROUP';

  const AGGREGATION_TYPES_MAP = {
    [FILTER_TYPES.PRODUCER]: AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_TYPE],
    [FILTER_TYPES.VENUE]: AGGREGATION_TYPES[FILTER_TYPES.VENUE],
    [FILTER_TYPES.ENSEMBLE]: AGGREGATION_TYPES[FILTER_TYPES.ENSEMBLE],
  };

  const contributionTypes = list.reduce((acc, item) => {
    const { name, slug, parentProfession } = item;
    const isPrimaryProfession = PRIMARY_PROFESSIONS_SLUGS.includes(slug);

    const key = isPrimaryProfession ? primaryProfessionsGroupKey : parentProfession?.id || commonGroupKey;

    const aggregationType = AGGREGATION_TYPES_MAP[slug] || DEFAULT_AGG_TYPE;

    let categoryValue = category || FILTER_TYPES.CONTRIBUTION_TYPE;

    if (slug === FILTER_TYPES.PRODUCER) {
      categoryValue = FILTER_TYPES.PRODUCER_TYPE;
    }

    const type =
      [FILTER_TYPES.PRODUCER, FILTER_TYPES.VENUE, FILTER_TYPES.CO_PRODUCER].includes(slug) || isPrimaryProfession
        ? slug
        : categoryValue;

    (acc[key] ||= []).push({
      ...item,
      type,
      label: name,
      filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
      aggregationType,
    });

    return acc;
  }, {});

  const primaryProfessionList = contributionTypes[primaryProfessionsGroupKey]?.sort((a, b) => {
    if (a.slug === FILTER_TYPES.DIRECTOR) {
      return -1;
    }

    if (b.slug === FILTER_TYPES.DIRECTOR) {
      return 1;
    }

    return 0;
  });

  return [
    primaryProfessionList,
    contributionTypes[commonGroupKey],
    contributionTypes[PROFESSION_IDS.SINGER],
    contributionTypes[PROFESSION_IDS.INSTRUMENTATION],
  ].filter(Boolean);
};

const transformProductionAggregationCoverAndDebut = data => {
  const DATA_MAPPING = {
    [AGGREGATION_TYPES[FILTER_TYPES.COVER_ROLE]]: {
      name: FILTER_TYPES.COVER_ROLE,
      label: `${TP}.FN_COVER_ROLE`,
      id: FILTER_TYPES.COVER_ROLE,
    },
    [AGGREGATION_TYPES[FILTER_TYPES.ROLE_DEBUT]]: {
      name: FILTER_TYPES.ROLE_DEBUT,
      label: `${TP}.FN_ROLE_DEBUT`,
      id: FILTER_TYPES.ROLE_DEBUT,
    },
    [AGGREGATION_TYPES[FILTER_TYPES.PRODUCER_DEBUT]]: {
      name: FILTER_TYPES.PRODUCER_DEBUT,
      label: `${TP}.FN_COMPANY_PRODUCER_DEBUT`,
      id: FILTER_TYPES.PRODUCER_DEBUT,
    },
  };

  return data?.map(option => {
    const { label, id } = DATA_MAPPING[option?.type];
    return {
      label,
      id,
      value: id,
      total: option?.data?.data?.[0]?.total,
      ...option,
    };
  });
};

export function transformProductionAggregationResponse({ selectedFilterType, aggregationType, response }) {
  if (!response?.data) {
    return { data: [], top5: [] };
  }

  if (selectedFilterType === FILTER_TYPES.COVER_DEBUT) {
    return transformProductionAggregationCoverAndDebut(response.data);
  }

  const getLabel = option => {
    switch (aggregationType) {
      case CLIENT_SIDE_AGGREGATION_TYPES.WORK:
        return getWorkTitle(option, true, true);
      default:
        return option.name;
    }
  };

  const getOptionsId = option =>
    selectedFilterType === FILTER_TYPES.ROLE_PROFESSION_REPERTOIRE
      ? option.workRole?.id || option.profession?.id
      : option.id;

  const includeAllProps = [
    FILTER_TYPES.ROLE_PROFESSION,
    FILTER_TYPES.CREATOR,
    FILTER_TYPES.WORK,
    FILTER_TYPES.ROLE_PROFESSION_REPERTOIRE,
    FILTER_TYPES.CONTRIBUTION_EXPRESSION,
    FILTER_TYPES.COUNTRY,
  ].includes(selectedFilterType);
  const requiresNestedOptions = selectedFilterType === FILTER_TYPES.WORK_TYPE;
  const isContributionExpression = selectedFilterType === FILTER_TYPES.CONTRIBUTION_EXPRESSION;

  return response.data.map(option => ({
    ...(includeAllProps && { ...option }),
    ...(isContributionExpression && {
      headline: option.headline,
    }),
    ...(requiresNestedOptions && { nestedFilterOptions: option.stagingTypes }),
    cityCountry: option.country?.name || option.city?.name ? getCityCountry(option) : undefined,
    id: getOptionsId(option),
    slug: option.slug,
    value: option.id || option.slug,
    label: getLabel(option),
    total: option.total,
  }));
}

export const transformProductionAggregationYears = (years = []) => {
  let startYear = years?.[years.length - 1]?.id;

  if (startYear < 1996) {
    startYear = 1996;
  }

  const endYear = years?.[0]?.id;

  return years.reduce((acc, item) => {
    if (item.id >= startYear && item.id <= endYear) {
      acc.push({
        ...item,
        key: item?.id,
        label: item?.name,
      });
    }

    return acc;
  }, []);
};

export const transformFeaturedHomepageComposers = response => {
  const { data } = response || {};

  return {
    ...response,
    data: data?.map(option => ({
      entity: {
        id: option?.id,
        name: option?.name || option?.original_name,
        slug: option?.slug,
        source_tag: option?.source_tag,
        headline: option?.headline,
        image: condenseImageObject(option?.image),
        // Add data needed for 4-line profile structure
        organizations: option?.organizations || [],
        professions: option?.professions || [],
        workTypes: option?.workTypes || [],
      },
      entityType: option?.entityType,
    })),
  };
};

export const selectFeaturedHomepageComposers = response => {
  const { data } = response || {};

  return data?.map(option => ({
    ...option,
    entity: {
      ...option?.entity,
      image: expandImageObject(option?.entity?.image),
    },
  }));
};
